import React, { useState } from "react";

// "How old are your target customers?" — gender toggle + two rows of cards
// (one per gender). Each option carries:
//   option.image           → female illustration
//   option.metadata.image_male → male illustration
//   option.label / label_arabic → age bucket text shown under the card
//
// Answer storage: { female: [optionId, ...], male: [optionId, ...] }
// (multi-select is implicit; allow_multiple is ignored for this type).
export const AgeGridField = ({ question, value, onChange, lang }) => {
  const opts = question.options || [];
  const [activeGender, setActiveGender] = useState("female");

  const v =
    value && typeof value === "object" && !Array.isArray(value)
      ? value
      : { female: [], male: [] };
  const female = Array.isArray(v.female) ? v.female : [];
  const male = Array.isArray(v.male) ? v.male : [];

  const toggle = (gender, id) => {
    const list = gender === "female" ? female : male;
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    onChange({ ...v, [gender]: next });
  };

  const isSel = (gender, id) =>
    (gender === "female" ? female : male).includes(id);

  const imgFor = (opt, gender) =>
    gender === "male"
      ? (opt.metadata && opt.metadata.image_male) || opt.image
      : opt.image;

  if (!opts.length) return <div className="dq-empty">No age buckets configured.</div>;

  const renderRow = (gender) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${opts.length}, 1fr)`,
        gap: 12,
        margin: "8px 0",
      }}
    >
      {opts.map((opt) => {
        const sel = isSel(gender, opt.id);
        const label =
          lang === "ar" && opt.label_arabic ? opt.label_arabic : opt.label;
        return (
          <button
            type="button"
            key={`${gender}-${opt.id}`}
            onClick={() => toggle(gender, opt.id)}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              padding: 8,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              opacity: activeGender === gender ? 1 : 0.5,
            }}
            disabled={activeGender !== gender}
          >
            {imgFor(opt, gender) ? (
              <img
                src={imgFor(opt, gender)}
                alt={label}
                style={{
                  maxHeight: 90,
                  maxWidth: "100%",
                  filter: activeGender === gender ? "none" : "grayscale(100%)",
                }}
              />
            ) : null}
            <span
              style={{
                padding: "4px 10px",
                background: sel ? "#000" : "#fff",
                color: sel ? "#fff" : "#000",
                border: "1px solid #000",
                borderRadius: 3,
                fontSize: 12,
                fontWeight: 600,
                minWidth: 80,
                textAlign: "center",
              }}
            >
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "center",
          marginBottom: 12,
        }}
      >
        {[
          ["female", lang === "ar" ? "أنثى" : "FEMALE"],
          ["male", lang === "ar" ? "ذكر" : "MALE"],
        ].map(([g, lbl]) => {
          const active = activeGender === g;
          return (
            <button
              type="button"
              key={g}
              onClick={() => setActiveGender(g)}
              style={{
                padding: "6px 24px",
                background: active ? "#000" : "#fff",
                color: active ? "#fff" : "#000",
                border: "1px solid #000",
                borderRadius: 3,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {lbl}
            </button>
          );
        })}
      </div>
      {renderRow("female")}
      <hr style={{ border: "none", borderTop: "1px solid #ddd", margin: "16px 0" }} />
      {renderRow("male")}
    </div>
  );
};
